'use client';

import { motion } from 'framer-motion';
import Image from 'next/image';
import BrandLogo from '@/components/common/BrandLogo';
import CTAButton from './CTAbutton';

interface HeroSectionProps {
  onCTAClick: (type: 'primary' | 'secondary') => void;
}

const ease = [0.16, 1, 0.3, 1] as const;

const HeroSection = ({ onCTAClick }: HeroSectionProps) => {
  return (
    <section
      id="hero"
      className="relative isolate min-h-[100svh] overflow-hidden bg-[var(--brand-ink)]"
    >
      <motion.div
        className="absolute inset-0 z-0"
        initial={{ scale: 1.06 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.8, ease }}
        aria-hidden
      >
        <Image
          src="/hero-atmosphere.png"
          alt=""
          fill
          priority
          sizes="100vw"
          className="object-cover object-[70%_center] sm:object-center"
          unoptimized
        />
      </motion.div>

      {/* Soft readability wash — keeps AI art visible */}
      <div
        className="absolute inset-0 z-[1]"
        style={{
          background: `
            linear-gradient(105deg, rgba(10,4,20,0.88) 0%, rgba(10,4,20,0.55) 45%, rgba(10,4,20,0.28) 72%, rgba(10,4,20,0.45) 100%),
            linear-gradient(to top, rgba(10,4,20,0.7) 0%, transparent 42%)
          `,
        }}
      />

      <div className="relative z-10 mx-auto flex min-h-[100svh] w-full max-w-7xl flex-col justify-end px-4 pb-20 pt-24 sm:justify-center sm:px-8 sm:pb-24 sm:pt-28 lg:px-16">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.14, delayChildren: 0.2 } },
          }}
          className="w-full max-w-2xl"
        >
          <motion.div
            variants={{
              hidden: { opacity: 0, y: 28 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.75, ease } },
            }}
            className="mb-6 sm:mb-8"
          >
            <BrandLogo
              tone="light"
              priority
              markSize={52}
              className="text-2xl sm:text-3xl"
            />
          </motion.div>

          <motion.h1
            variants={{
              hidden: { opacity: 0, y: 28 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.75, ease } },
            }}
            className="mb-4 font-display text-[2rem] font-extrabold leading-[1.08] tracking-tight text-white sm:mb-5 sm:text-5xl lg:text-6xl"
          >
            Market-ready MVPs in{' '}
            <span className="bg-gradient-to-r from-[#FCA000] to-[#FFC44D] bg-clip-text text-transparent">
              28 days
            </span>
            .
          </motion.h1>

          <motion.p
            variants={{
              hidden: { opacity: 0, y: 28 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.75, ease } },
            }}
            className="mb-8 max-w-lg text-[0.95rem] leading-relaxed text-white/75 sm:mb-10 sm:text-lg"
          >
            Stop wasting months on development. Launch, validate with real users, and move faster
            without compromising quality.
          </motion.p>

          <motion.div
            variants={{
              hidden: { opacity: 0, y: 28 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.75, ease } },
            }}
            className="flex w-full flex-col gap-3 sm:flex-row sm:items-center"
          >
            <CTAButton
              type="primary"
              label="Start Your MVP"
              icon="ArrowRightIcon"
              onClick={() => onCTAClick('primary')}
              className="w-full !bg-[var(--brand-orange)] !text-[var(--brand-ink)] hover:!bg-[#ffb01a] sm:w-auto"
            />
            <CTAButton
              type="secondary"
              label="See How It Works"
              onClick={() => onCTAClick('secondary')}
              className="w-full !border-white/35 !text-white hover:!border-white hover:!bg-white/10 sm:w-auto"
            />
          </motion.div>
        </motion.div>
      </div>

      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 z-[2] h-1"
        style={{
          background: 'linear-gradient(90deg, #7808D8 0%, #FCA000 55%, transparent 100%)',
        }}
        aria-hidden
      />
    </section>
  );
};

export default HeroSection;
